import { metronome } from './metronome';

export type ScheduleActionOptions = {
    id: string;
    delay: number;
    action: (elapsed: number) => void;
};

type ScheduledAction = ScheduleActionOptions & {
    scheduledAt: number;
    destroy?: () => void;
};

type InactivityTimerState = 'uninitialized' | 'running' | 'stopped' | 'destroyed';

const DEFAULT_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
const DEFAULT_THROTTLE = 1000;

export class InactivityTimer {
    private lastActivityTime: number;
    private scheduledActions: Map<string, ScheduledAction>;
    private events: string[];
    private throttle: number;
    private throttleTimeout: NodeJS.Timeout | null = null;
    private state: InactivityTimerState = 'uninitialized';
    private activityTrackingPaused = false;

    constructor() {
        this.lastActivityTime = Date.now();
        this.scheduledActions = new Map();
        this.events = DEFAULT_EVENTS;
        this.throttle = DEFAULT_THROTTLE;

        this.handleActivity = this.handleActivity.bind(this);
    }

    public init = (): void => {
        if (this.state !== 'uninitialized') return;
        this.start();
    };

    public start = (): void => {
        if (['running'].includes(this.state)) return;

        this.subscribeToActivities();
        this.state = 'running';

        this.activityTrackingPaused = false;
        this.lastActivityTime = Date.now();
        this.rescheduleAllActions();
    };

    public stop = (): void => {
        if (this.state !== 'running') return;
        this.suspend('stopped');
    };

    public destroy = (): void => {
        this.suspend(this.state === 'stopped' ? 'stopped' : 'uninitialized');
    };

    public scheduleAction = (options: ScheduleActionOptions): void => {
        if (this.state === 'destroyed') {
            console.error('Cannot schedule action: timer service is destroyed');
            return;
        }

        this.cancelAction(options.id);

        const scheduledAction: ScheduledAction = {
            id: options.id,
            delay: options.delay,
            action: options.action,
            scheduledAt: Date.now(),
        };

        if (this.state === 'running') {
            scheduledAction.destroy = metronome(
                (elapsed) => {
                    options.action(elapsed);
                    this.scheduledActions.delete(options.id);
                },
                options.delay,
                { tickOnce: true },
            );
        }

        this.scheduledActions.set(options.id, scheduledAction);
    };

    // Once the user has been warned, only an explicit response may extend the session —
    // otherwise moving the mouse to reach the dialog silently defers the pending sign-out.
    public pauseActivityTracking = (): void => {
        this.activityTrackingPaused = true;
    };

    public resumeActivityTracking = (): void => {
        this.activityTrackingPaused = false;
    };

    public cancelAction = (id: string): void => {
        const scheduledAction = this.scheduledActions.get(id);
        scheduledAction?.destroy?.();
        this.scheduledActions.delete(id);
    };

    public getIdleTime = (): number => {
        if (this.state !== 'running') return 0;
        return Date.now() - this.lastActivityTime;
    };

    private suspend = (suspendState: 'stopped' | 'uninitialized'): void => {
        if (this.throttleTimeout) {
            clearTimeout(this.throttleTimeout);
            this.throttleTimeout = null;
        }

        this.suspendAllActions();
        this.unsubscribeFromActivities();

        this.state = suspendState;
    };

    private subscribeToActivities = (): void => {
        if (typeof window === 'undefined') return;
        this.events.forEach((event) => {
            window.addEventListener(event, this.handleActivity, { passive: true });
        });
    };

    private unsubscribeFromActivities = (): void => {
        if (typeof window === 'undefined') return;
        this.events.forEach((event) => {
            window.removeEventListener(event, this.handleActivity);
        });
    };

    private handleActivity = (): void => {
        if (this.state !== 'running' || this.activityTrackingPaused) return;

        if (this.throttleTimeout) return;

        this.throttleTimeout = setTimeout(() => {
            this.throttleTimeout = null;
        }, this.throttle);

        this.lastActivityTime = Date.now();
        this.rescheduleAllActions();
    };

    private suspendAllActions = (): void => {
        this.scheduledActions.forEach((scheduledAction) => {
            scheduledAction.destroy?.();
            scheduledAction.destroy = undefined;
        });
    };

    private rescheduleAllActions = (): void => {
        this.scheduledActions.forEach((scheduledAction) => {
            scheduledAction.destroy?.();

            scheduledAction.scheduledAt = Date.now();
            scheduledAction.destroy = metronome(
                (elapsed) => {
                    scheduledAction.action(elapsed);
                    this.scheduledActions.delete(scheduledAction.id);
                },
                scheduledAction.delay,
                { tickOnce: true },
            );
        });
    };
}
